import React, { useState, useEffect } from 'react';
import { Button, Col, Input, Modal, Row, notification, Spin } from 'antd';
import CustomInput from 'Components/Form/CustomInput/CustomInput';
import CustomInputNumber from 'Components/Form/CustomInputNumber/CustomInputNumber';
import CustomCheckbox from 'Components/Form/CustomCheckbox/CustomCheckbox';
import CustomDatePicker from 'Components/Form/CustomDatePicker/CustomDatePicker';
import CustomSelect from 'Components/Form/CustomSelect/CustomSelect';
import { useAppDispatch } from 'Redux/store';
import { useSelector } from 'react-redux';
import {
  getCoordinateByLocation,
  getRouteDistanceAndDuration,
} from 'Redux/Location/reducer';
import { selectCurrentCoordinate } from 'Redux/Location/slice';
import { createOrder } from 'Redux/Order/reducer';
import { selectOrderLoading } from 'Redux/Order/slice';
import { Controller, useForm } from 'react-hook-form';
import {
  CaretDownFilled,
  AimOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  dimsToString,
  dateToISOString,
  camelCaseToFormName,
} from 'Shared/Utils';
import { ICoordinate } from 'Shared/Types/coordinate';
import { IDistanceAndDuration } from 'Shared/Types/distance';
import useDebounce from 'Shared/Hooks/useDebounce';
import usePrevious from 'Shared/Hooks/usePrevious';
import {
  NewLoadVisibilityFlags,
  ToggleVisibilityParams,
} from 'Shared/Types/form';
import styles from './NewLoad.module.scss';

interface NewLoadProps {
  visible: boolean;
  onCancel: () => void;
}

export const NewLoad: React.FC<NewLoadProps> = ({ visible, onCancel }) => {
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isSubmitting },
    clearErrors,
    setFocus,
  } = useForm();
  const [visibilityFlags, setVisibilityFlags] =
    useState<NewLoadVisibilityFlags>({
      expires: false,
      pick_up_date: false,
      deliver_date: false,
      vehicle_required: false,
    });
  const [modalVisibility, setModalVisibility] = useState(true);
  const [notificationAPI, contextHolder] = notification.useNotification();
  const currentCoordinate = useSelector(selectCurrentCoordinate);
  const [selectedCoordinates, setSelectedCoordinates] = useState<
    Record<string, ICoordinate>
  >({
    pickUpAt: {
      lat: '',
      lon: '',
      isMultipleChoice: false,
      isValid: false,
      fieldName: 'pickUpAt',
    },
    deliverTo: {
      lat: '',
      lon: '',
      isMultipleChoice: false,
      isValid: false,
      fieldName: 'deliverTo',
    },
  });
  const watchFields = watch(['pick_up_at', 'deliver_to']);
  const watchObject = {
    pickUpAt: {
      current: watchFields[0],
      previous: usePrevious(watchFields[0]),
    },
    deliverTo: {
      current: watchFields[1],
      previous: usePrevious(watchFields[1]),
    },
  };
  const sendingNewLoad = useSelector(selectOrderLoading);

  const toggleVisibility = ({
    flagName,
    setToFalse = false,
  }: ToggleVisibilityParams) => {
    setVisibilityFlags((prevState) => ({
      ...prevState,
      [flagName]: setToFalse ? false : !prevState[flagName],
    }));
  };

  useEffect(() => {
    if (currentCoordinate) {
      const { fieldName } = currentCoordinate;
      const formFieldName = camelCaseToFormName(fieldName);

      setSelectedCoordinates((prevState) => ({
        ...prevState,
        [fieldName]: currentCoordinate,
      }));

      let errorText;

      if (!currentCoordinate.isValid) {
        errorText = 'Location is invalid. Please check your data.';
      }

      if (currentCoordinate.isMultipleChoice) {
        errorText = 'Specify a more precise location and/or add a postcode.';
      }

      if (errorText) {
        setError(formFieldName, {
          type: 'range',
          message: errorText,
        });
        setFocus(formFieldName);
      } else {
        clearErrors(formFieldName);
      }
    }
  }, [currentCoordinate]);

  useDebounce(
    () => {
      const { pickUpAt, deliverTo } = watchObject;

      if (pickUpAt.current && pickUpAt.current !== pickUpAt.previous) {
        dispatch(
          getCoordinateByLocation({
            location: pickUpAt.current,
            fieldName: 'pickUpAt',
          }),
        );
      }

      if (deliverTo.current && deliverTo.current !== deliverTo.previous) {
        dispatch(
          getCoordinateByLocation({
            location: deliverTo.current,
            fieldName: 'deliverTo',
          }),
        );
      }
    },
    errors ? 0 : 3000,
    [watchFields],
  );

  const addLoadHandler = async (values: any) => {
    const { pickUpAt, deliverTo } = selectedCoordinates;
    const coordinateFrom = [pickUpAt.lat, pickUpAt.lon].join(',');
    const coordinateTo = [deliverTo.lat, deliverTo.lon].join(',');

    try {
      const { payload } = await dispatch(
        getRouteDistanceAndDuration({
          coordinateFrom,
          coordinateTo,
        }),
      );

      const { distanceInMi, durationInSec } = payload as IDistanceAndDuration;

      if (distanceInMi <= 0) {
        setError('pick_up_at', {
          type: 'range',
          message:
            'There are no driving routes from pick-up to delivery locations.',
        });
        setFocus('pick_up_at');
        return;
      }

      const { expires, pick_up_date, deliver_date } = values;
      expires.$d.setMilliseconds(0);
      pick_up_date.$d.setMilliseconds(0);
      deliver_date.$d.setMilliseconds(0);

      if (expires.$d.getTime() <= new Date().getTime()) {
        setError('expires', {
          type: 'range',
          message: 'Should be greater than the current date and time.',
        });
        setFocus('expires');
        return;
      }

      if (expires > pick_up_date) {
        setError('expires', {
          type: 'range',
          message:
            'Should be lower than or equal to the pick-up date and time.',
        });
        setFocus('expires');
        return;
      }

      if (pick_up_date >= deliver_date) {
        setError('pick_up_date', {
          type: 'range',
          message: 'Should be lower than the delivery date and time.',
        });
        setFocus('pick_up_date');
        return;
      }

      values.expires = dateToISOString(expires);
      values.pick_up_date = dateToISOString(pick_up_date);
      values.deliver_date = dateToISOString(deliver_date);
      values.dimensions = dimsToString(values.dimensions);
      values.weight = values.weight ? values.weight + ' lbs' : undefined;
      values.vehicle_required = values.vehicle_required?.length
        ? values.vehicle_required.join(' or ')
        : undefined;
      values.coordinate_from = coordinateFrom;
      values.coordinate_to = coordinateTo;
      values.transit_distance = distanceInMi;
      values.transit_time = durationInSec;

      console.log('New load form values =>', values);

      const response = await dispatch(createOrder(values));

      if (response.payload) {
        notificationAPI.success({
          message: 'Load was successfully placed',
          duration: 2,
        });
        reset();
        setModalVisibility(false);
      } else {
        notificationAPI.error({
          message: "Internal error, load wasn't placed",
          duration: 4,
        });
      }
    } catch (error) {
      console.error('New load handling error:', error);
      notificationAPI.error({
        message: 'An error occurred while placing the load',
        duration: 4,
      });
    }
  };

  return (
    <Modal
      title={<p className={styles.addLoad__title}>New Load</p>}
      open={modalVisibility && visible}
      footer={true}
      maskClosable={true}
      width={440}
      onCancel={() => {
        Object.keys(visibilityFlags).forEach((flagName) => {
          toggleVisibility({
            flagName: flagName as keyof NewLoadVisibilityFlags,
            setToFalse: true,
          });
        });

        if (!Object.values(visibilityFlags).find(Boolean)) {
          onCancel();
        }
      }}
      className={styles.addLoad}
    >
      {contextHolder}
      <form noValidate onSubmit={handleSubmit(addLoadHandler)}>
        <Controller
          name="broker"
          control={control}
          render={({ field }) => (
            <CustomInput
              field={field}
              fieldName={field.name}
              labelName="Broker company name"
            />
          )}
        />

        <Controller
          name="broker_name"
          control={control}
          rules={{ required: 'Please fill out this field.' }}
          render={({ field }) => (
            <CustomInput
              field={field}
              fieldName={field.name}
              labelName="Broker responsible name *"
              error={errors[field.name]}
            />
          )}
        />

        <div className={styles.addLoad__broker}>
          <div>
            <Controller
              name="broker_phone"
              control={control}
              render={({ field }) => (
                <CustomInput
                  field={field}
                  fieldName={field.name}
                  labelName="Broker phone number"
                  type="tel"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Please fill out this field.',
                pattern: {
                  value:
                    /^(?=.{1,256})(?=.{1,64}@.{1,255}$)[A-Za-z0-9._%-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/,
                  message: 'Invalid email address.',
                },
              }}
              render={({ field }) => (
                <CustomInput
                  field={field}
                  fieldName={field.name}
                  labelName="Broker email *"
                  error={errors[field.name]}
                  type="email"
                />
              )}
            />
          </div>
        </div>

        <label htmlFor="expires">Expires *</label>
        <Controller
          name="expires"
          control={control}
          rules={{ required: 'Please fill out this field.' }}
          render={({ field }) => (
            <CustomDatePicker
              field={field}
              fieldName={field.name}
              visibilityToggler={toggleVisibility}
              visibilityFlagValue={visibilityFlags[field.name]}
              placeholder="Select date & time"
              suffix={CalendarOutlined}
              error={errors[field.name]}
            />
          )}
        />

        <label>Pick-up *</label>
        <Controller
          name="pick_up_at"
          control={control}
          rules={{ required: 'Please fill out this field.' }}
          render={({ field }) => (
            <CustomInput
              field={field}
              fieldName={field.name}
              placeholder="Enter a location or/and a post code"
              suffix={AimOutlined}
              error={errors[field.name]}
            />
          )}
        />
        <Controller
          name="pick_up_date"
          control={control}
          rules={{ required: 'Please fill out this field.' }}
          render={({ field }) => (
            <CustomDatePicker
              field={field}
              fieldName={field.name}
              visibilityToggler={toggleVisibility}
              visibilityFlagValue={visibilityFlags[field.name]}
              placeholder="Select date & time"
              suffix={CalendarOutlined}
              error={errors[field.name]}
            />
          )}
        />

        <label>Delivery *</label>
        <Controller
          name="deliver_to"
          control={control}
          rules={{ required: 'Please fill out this field.' }}
          render={({ field }) => (
            <CustomInput
              field={field}
              fieldName={field.name}
              placeholder="Enter a location or/and a post code"
              suffix={AimOutlined}
              error={errors[field.name]}
            />
          )}
        />
        <Controller
          name="deliver_date"
          control={control}
          rules={{ required: 'Please fill out this field.' }}
          render={({ field }) => (
            <CustomDatePicker
              field={field}
              fieldName={field.name}
              visibilityToggler={toggleVisibility}
              visibilityFlagValue={visibilityFlags[field.name]}
              placeholder="Select date & time"
              suffix={CalendarOutlined}
              error={errors[field.name]}
            />
          )}
        />

        <Controller
          control={control}
          name="order_number"
          render={({ field }) => (
            <CustomInput
              field={field}
              fieldName={field.name}
              labelName="Order number"
              autoComplete="off"
            />
          )}
        />

        <label>Dims (in)</label>
        <div className={styles.addLoad__dims}>
          <Controller
            name="dimensions"
            control={control}
            render={({ field }) => (
              <>
                <CustomInputNumber
                  field={field}
                  fieldName={'load_length'}
                  placeholder={'Length'}
                  customStyles={styles.addLoad__dims__input}
                />
                <CustomInputNumber
                  field={field}
                  fieldName={'load_width'}
                  placeholder={'Width'}
                  customStyles={styles.addLoad__dims__input}
                />
                <CustomInputNumber
                  field={field}
                  fieldName={'load_height'}
                  placeholder={'Height'}
                  customStyles={styles.addLoad__dims__input}
                />
              </>
            )}
          />
          <Controller
            name="weight"
            control={control}
            render={({ field }) => (
              <CustomInputNumber
                field={field}
                fieldName={field.name}
                placeholder={'Weight'}
                customStyles={styles.addLoad__dims__input}
              />
            )}
          />
          <Controller
            name="pieces"
            control={control}
            render={({ field }) => (
              <CustomInputNumber
                field={field}
                fieldName={field.name}
                placeholder={'Pieces'}
                customStyles={styles.addLoad__dims__input}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="vehicle_required"
          rules={{ shouldUnregister: true }}
          render={({ field }) => (
            <CustomSelect
              field={field}
              fieldName={field.name}
              visibilityToggler={toggleVisibility}
              visibilityFlagValue={visibilityFlags[field.name]}
              labelName="Vehicle type"
              suffix={CaretDownFilled}
            />
          )}
        />

        <label className={styles.addLoad__noteTitle} htmlFor="notes">
          Note
        </label>
        <Controller
          control={control}
          name="notes"
          rules={{ shouldUnregister: true }}
          render={({ field }) => (
            <Input.TextArea
              id="notes"
              className={styles.addLoad__notes}
              autoSize={{ minRows: 5 }}
              {...field}
            />
          )}
        />

        <div className={styles.addLoad__checkboxes}>
          <Row>
            <Col md={12}>
              <Controller
                control={control}
                name="dock_level"
                render={({ field }) => (
                  <CustomCheckbox field={field} labelName="Dock level" />
                )}
              />
            </Col>
            <Col md={12}>
              <Controller
                control={control}
                name="fast_load"
                render={({ field }) => (
                  <CustomCheckbox field={field} labelName="Fast load" />
                )}
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Controller
                control={control}
                name="hazmat"
                render={({ field }) => (
                  <CustomCheckbox field={field} labelName="Hazardous" />
                )}
              />
            </Col>
            <Col md={12}>
              <Controller
                control={control}
                name="stackable"
                render={({ field }) => (
                  <CustomCheckbox field={field} labelName="Stackable" />
                )}
              />
            </Col>
          </Row>
        </div>

        <Button
          className={styles.addLoad__btn}
          htmlType="submit"
          disabled={
            !selectedCoordinates.pickUpAt.isValid ||
            !selectedCoordinates.deliverTo.isValid ||
            isSubmitting
          }
          title="Fill in required fields with the * mark."
        >
          {isSubmitting ? <Spin /> : 'Save'}
        </Button>
      </form>
    </Modal>
  );
};
